import { useMemo, useState } from "react";
import type { SelectItem } from "@mantine/core";
interface Props<K extends SelectItem> {
  data: K[];
  grouped: boolean | undefined;
}
const useCheckboxGroupData = <K extends SelectItem>({
  data,
  grouped,
}: Props<K>) => {
  const [searchStr, setSearchStr] = useState("");

  const searchCondition = (label: string | undefined) =>
    label?.toLowerCase().includes(searchStr.toLowerCase());

  const groupedData = useMemo(() => {
    if (!grouped || !data) return {};

    return data.reduce((acc: Record<string, K[]>, item: K) => {
      const hasSearchTerm = searchStr.trim().length > 0;
      const itemMatchesSearch = searchCondition(item.label);
      const groupMatchesSearch = item.group && searchCondition(item.group);

      // If there's a search term, only include items that match the search or whose group matches
      if (hasSearchTerm && !itemMatchesSearch && !groupMatchesSearch) {
        return acc;
      }

      if (item.group) {
        // Skip items that have the same label as their group (these are group-level items)
        if (item.label === item.group) return acc;

        if (!acc[item.group]) acc[item.group] = [];
        acc[item.group].push(item);
      } else {
        if (!acc.ungrouped) acc.ungrouped = [];
        acc.ungrouped.push(item);
      }

      return acc;
    }, {});
  }, [grouped, data, searchStr]);

  const ungroupedData = useMemo(() => {
    if (grouped) return [];
    const result: K[] = [];
    data.forEach(item => {
      if (searchStr.trim().length > 0) {
        if (
          item.parts &&
          item.parts?.some((part: any) => searchCondition(part.label)) &&
          !searchCondition(item.label)
        ) {
          const newParts = item.parts.filter((part: any) =>
            searchCondition(part.label),
          );
          result.push({ ...item, parts: newParts });
        } else {
          if (searchCondition(item.label)) result.push(item);
        }
      } else {
        result.push(item);
      }
    });
    return result;
  }, [grouped, data, searchStr]);
  return {
    groupedData,
    ungroupedData,
    searchStr,
    setSearchStr,
  };
};
export default useCheckboxGroupData;
